import { SITE_URL, BRAND } from "@/lib/site";

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "LocalBusiness", "ProfessionalService", "PhotographyBusiness"],
      "@id": `${SITE_URL}/#organization`,
      name: BRAND.name,
      legalName: BRAND.name,
      alternateName: BRAND.shortName,
      description: BRAND.description,
      url: SITE_URL,
      logo: `${SITE_URL}/logo192.png`,
      image: [
        "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80"
      ],
      email: BRAND.email,
      telephone: BRAND.phoneFormatted[0],
      priceRange: "$$$",
      currenciesAccepted: "INR",
      paymentAccepted: "Cash, Bank Transfer, UPI",
      foundingDate: BRAND.founded,
      hasMap: "https://maps.google.com/maps?q=DMS+Productions+%26+Films,+Duplex+No.+29,+Duplex+Society,+Bhimpur,+Forest+Park,+Bhubaneswar,+Odisha+751020,+India",
      knowsAbout: [
        "Luxury Wedding Photography",
        "Pre Wedding Shoot Photography",
        "Cinematic Wedding Films",
        "Editorial Wedding Photography",
        "Destination Wedding Photography in India",
        "Bhubaneswar Wedding Photography",
        "Odisha Wedding Stories"
      ],
      address: {
        "@type": "PostalAddress",
        streetAddress: BRAND.address.street,
        addressLocality: BRAND.address.locality,
        addressRegion: BRAND.address.region,
        postalCode: BRAND.address.postalCode,
        addressCountry: BRAND.address.country,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: BRAND.address.geo.latitude,
        longitude: BRAND.address.geo.longitude,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          opens: "09:00",
          closes: "21:00",
        },
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "128",
        bestRating: "5",
      },
      areaServed: BRAND.serviceAreas.map((area) => ({
        "@type": "AdministrativeArea",
        name: area,
      })),
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: BRAND.phoneFormatted[0],
          contactType: "customer service",
          availableLanguage: ["English", "Hindi", "Odia"],
          areaServed: "IN",
        },
        {
          "@type": "ContactPoint",
          telephone: BRAND.phoneFormatted[1],
          contactType: "reservations",
          availableLanguage: ["English", "Hindi", "Odia"],
          areaServed: "IN",
        },
      ],
      sameAs: Object.values(BRAND.social),
    },
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: BRAND.name,
  description: BRAND.description,
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

export const webPageSchema = (name, path, description) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}${path}#webpage`,
  name,
  description,
  url: `${SITE_URL}${path}`,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#organization` },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const serviceSchema = (svc) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: svc.title,
  name: `${svc.title} in Bhubaneswar & Odisha`,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: BRAND.serviceAreas.map((area) => ({
    "@type": "AdministrativeArea",
    name: area,
  })),
  description: svc.promise || svc.excerpt,
  url: `${SITE_URL}/services/${svc.slug}`,
  offers: {
    "@type": "Offer",
    priceCurrency: "INR",
    availability: "https://schema.org/InStock",
  },
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": ["BlogPosting", "Article"],
  "@id": `${SITE_URL}/blog/${post.slug}#article`,
  headline: post.title,
  description: post.excerpt,
  image: post.cover,
  datePublished: post.date,
  dateModified: post.date,
  author: {
    "@type": "Organization",
    name: BRAND.name,
    url: SITE_URL,
  },
  publisher: { "@id": `${SITE_URL}/#organization` },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}/blog/${post.slug}`,
  },
});

export const imageObjectSchema = (imageUrl, caption, pageUrl) => ({
  "@context": "https://schema.org",
  "@type": "ImageObject",
  contentUrl: imageUrl,
  url: imageUrl,
  caption: caption,
  creator: { "@id": `${SITE_URL}/#organization` },
  copyrightNotice: `© ${new Date().getFullYear()} ${BRAND.name}`,
});

export const contactPointSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `Contact ${BRAND.name}`,
  description: `Contact DMS Productions & Films for luxury wedding photography inquiries in Bhubaneswar, Odisha & destination weddings.`,
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@id": `${SITE_URL}/#organization`,
  },
});

export const videoSchema = (film) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: `${film.title} — Cinematic Wedding Film`,
  description: `Cinematic wedding film of ${film.title} in ${film.location} by ${BRAND.name}.`,
  thumbnailUrl: film.poster,
  uploadDate: "2025-01-01",
  contentUrl: `https://www.youtube.com/watch?v=${film.youtubeId}`,
  embedUrl: `https://www.youtube.com/embed/${film.youtubeId}`,
});
